import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { LanguageProvider } from '@/lib/i18n';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import Landing from '@/pages/Landing';
import Pricing from '@/pages/Pricing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import GoogleAuth from '@/pages/GoogleAuth';
import Onboarding from '@/pages/Onboarding';
import Dashboard from '@/pages/Dashboard';
import Upload from '@/pages/Upload';
import VideoClips from '@/pages/VideoClips';
import Editor from '@/pages/Editor';
import ShareClip from '@/pages/ShareClip';
import Mentions from '@/pages/legal/Mentions';
import Terms from '@/pages/legal/Terms';
import Privacy from '@/pages/legal/Privacy';
import CookiesPolicy from '@/pages/legal/Cookies';
import Admin from '@/pages/Admin';
import PipelinePerformance from '@/pages/admin/PipelinePerformance';
import BetaAccess from '@/pages/admin/BetaAccess';
import Credits from '@/pages/Credits';
import PublishingHistory from '@/pages/social/PublishingHistory';
import GrowthDashboard from '@/pages/growth/GrowthDashboard';
import AIMemoryDashboard from '@/pages/memory/AIMemoryDashboard';
import Publishing from '@/pages/Publishing';
import BillingSuccess from '@/pages/BillingSuccess';
import ShowcasePage from '@/pages/Showcase';

const SplashLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-black" data-testid="splash-loader">
    <div className="w-14 h-14 rounded-xl bg-[#D4FF00] flex items-center justify-center mb-5 animate-pulse" style={{ boxShadow: '0 0 40px rgba(212,255,0,0.4)' }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 9 22 9 17 14 19 22 12 18 5 22 7 14 2 9 9 9 12 2" /></svg>
    </div>
    <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4FF00]">HighlightAI</span>
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">Chargement...</span>
    <div className="w-32 h-0.5 bg-white/5 rounded-full overflow-hidden mt-4">
      <div className="h-full bg-[#D4FF00] animate-pulse" style={{ width: '40%' }} />
    </div>
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <SplashLoader />;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  return children;
};

const Shell = ({ children, hideFooter = false }) => (
  <div className="App grain min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1">{children}</main>
    {!hideFooter && <Footer />}
  </div>
);

function App() {
  return (
    <ErrorBoundary>
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Shell><Landing /></Shell>} />
          <Route path="/pricing" element={<Shell><Pricing /></Shell>} />
          <Route path="/showcase" element={<Shell><ShowcasePage /></Shell>} />
          <Route path="/login" element={<Shell hideFooter><Login /></Shell>} />
          <Route path="/register" element={<Shell hideFooter><Register /></Shell>} />
          <Route path="/auth/google/callback" element={<GoogleAuth />} />
          <Route path="/onboarding" element={<Protected><Shell hideFooter><Onboarding /></Shell></Protected>} />
          <Route path="/dashboard" element={<Protected><Shell><Dashboard /></Shell></Protected>} />
          <Route path="/upload" element={<Protected><Shell><Upload /></Shell></Protected>} />
          <Route path="/videos/:id" element={<Protected><Shell><VideoClips /></Shell></Protected>} />
          <Route path="/clips/:id" element={<Protected><Shell><Editor /></Shell></Protected>} />
          <Route path="/publishing" element={<Protected><Shell><Publishing /></Shell></Protected>} />
          <Route path="/share/:id" element={<ShareClip />} />
          <Route path="/legal/mentions" element={<Mentions />} />
          <Route path="/legal/cgv" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/cookies" element={<CookiesPolicy />} />
          <Route path="/admin" element={<Protected><Shell><Admin /></Shell></Protected>} />
          <Route path="/admin/performance" element={<Protected><Shell><PipelinePerformance /></Shell></Protected>} />
          <Route path="/admin/beta" element={<Protected><Shell><BetaAccess /></Shell></Protected>} />
          <Route path="/credits" element={<Protected><Shell><Credits /></Shell></Protected>} />
          <Route path="/social/history" element={<Protected><Shell><PublishingHistory /></Shell></Protected>} />
          <Route path="/growth" element={<Protected><Shell><GrowthDashboard /></Shell></Protected>} />
          <Route path="/memory" element={<Protected><Shell><AIMemoryDashboard /></Shell></Protected>} />
          <Route path="/billing/success" element={<Protected><Shell hideFooter><BillingSuccess /></Shell></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
